import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import {
  FolderOpen,
  Visibility,
  Delete,
  PictureAsPdf,
  TableChart,
  Warning,
} from '@mui/icons-material';
import { Document, AnalyzedBrick, Pageable } from '../types';
import { apiService } from '../services/apiService';
import { mockDocumentsData } from '../data/mockDocuments';
import { useTokenState } from '../App';


const ITEMS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

const Documents: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Paginierung State
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [paginationMeta, setPaginationMeta] = useState<Pageable | null>(null);

  const { isTokenValid, tokenState } = useTokenState();

  useEffect(() => {
    // Warte bis Token gültig ist, bevor API-Aufrufe gemacht werden
    if (!isTokenValid) {
      if (tokenState === 'idle' || tokenState === 'checking') {
        setLoading(true);
      } else {
        setLoading(false);
      }
      return;
    }

    loadDocuments(page, limit);
  }, [isTokenValid, tokenState, page, limit]);

  const loadDocuments = async (currentPage: number, currentLimit: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getDocuments(currentPage, currentLimit);

      // Validiere dass data die erwartete Struktur hat
      if (data && Array.isArray(data.items)) {
        setDocuments(data.items);
        setPaginationMeta(data.meta);
      } else {
        console.warn('API returned invalid data format, using mock data:', data);
        setDocuments(mockDocumentsData);
        setPaginationMeta(null);
      }
    } catch (err) {
      console.warn('Fehler beim Laden der Dokumente, verwende Mock-Daten:', err);
      // Fallback zu Mock-Daten wenn Backend nicht verfügbar
      setDocuments(mockDocumentsData);
      setPaginationMeta(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (event: SelectChangeEvent<number>) => {
    setLimit(Number(event.target.value));
    setPage(1); // Reset zur ersten Seite bei Änderung der Seitengröße
  };

  const handleViewDetails = (doc: Document) => {
    setSelectedDoc(doc);
  };

  const handleCloseDialog = () => {
    setSelectedDoc(null);
  };

  const handleDelete = (docId: string) => {
    // TODO: Implement DELETE endpoint in API
    // For now, just update local state
    const updated = documents.filter((doc) => doc.id !== docId);
    setDocuments(updated);
    // Keep localStorage as fallback until API is ready
    localStorage.setItem('documents', JSON.stringify(updated));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ bgcolor: '#f8fafc', minHeight: 'calc(100vh - 64px)', py: 4 }}>
        <Container maxWidth="xl">
          <Card elevation={2}>
            <CardContent sx={{ py: 8, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                Dokumente werden geladen...
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ bgcolor: '#f8fafc', minHeight: 'calc(100vh - 64px)', py: 4 }}>
        <Container maxWidth="xl">
          <Card elevation={2}>
            <CardContent sx={{ py: 8, textAlign: 'center' }}>
              <Warning sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
              <Typography variant="h6" color="error" gutterBottom>
                Fehler beim Laden
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {error}
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: 'calc(100vh - 64px)', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <FolderOpen sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" fontWeight={700}>
              Meine Dokumente
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Übersicht aller analysierten Dokumente
          </Typography>
        </Box>

        {/* Statistics */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Statistik
            </Typography>
            <Typography variant="body1" color="text.secondary">
              <strong>{paginationMeta?.totalItems ?? documents.length}</strong> Dokumente analysiert
              {paginationMeta && paginationMeta.totalPages > 1 && (
                <> &bull; Seite {paginationMeta.page} von {paginationMeta.totalPages}</>
              )}
            </Typography>
          </CardContent>
        </Card>

        {/* Documents Table */}
        <Card elevation={2}>
          <CardContent>
            {documents.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <FolderOpen sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Keine Dokumente vorhanden
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Analysieren Sie Ihr erstes Dokument auf der Upload-Seite
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Dateiname</strong></TableCell>
                        <TableCell><strong>Typ</strong></TableCell>
                        <TableCell><strong>Upload-Datum</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell align="right"><strong>Gesamt-Steine</strong></TableCell>
                        <TableCell align="right"><strong>Geschützt</strong></TableCell>
                        <TableCell align="center"><strong>Aktionen</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {documents.map((doc) => (
                        <TableRow key={doc.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {doc.fileType === 'pdf' ? (
                                <PictureAsPdf color="error" />
                              ) : (
                                <TableChart color="success" />
                              )}
                              <Typography variant="body2">{doc.filename}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={doc.fileType.toUpperCase()}
                              size="small"
                              color={doc.fileType === 'pdf' ? 'error' : 'success'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{formatDate(doc.uploadDate)}</TableCell>
                          <TableCell>
                            <Chip
                              label={doc.status === 'analyzed' ? 'Analysiert' : doc.status}
                              color={doc.status === 'analyzed' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <strong>{doc.totalBricks || 0}</strong>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={doc.protectedBricks !== '0' && doc.protectedBricks !== 'ANALYZING' ? 'error.main' : 'success.main'}
                            >
                              {doc.protectedBricks || '0'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewDetails(doc)}
                            >
                              <Visibility />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(doc.id)}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                {paginationMeta && paginationMeta.totalPages > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 3,
                      flexWrap: 'wrap',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="items-per-page-label">Pro Seite</InputLabel>
                        <Select
                          labelId="items-per-page-label"
                          value={limit}
                          label="Pro Seite"
                          onChange={handleLimitChange}
                        >
                          {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="body2" color="text.secondary">
                        {paginationMeta.pagingCounter ?? ((page - 1) * limit + 1)} -{' '}
                        {Math.min(
                          (paginationMeta.pagingCounter ?? ((page - 1) * limit + 1)) + documents.length - 1,
                          paginationMeta.totalItems
                        )}{' '}
                        von {paginationMeta.totalItems}
                      </Typography>
                    </Box>

                    <Pagination
                      count={paginationMeta.totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                      siblingCount={1}
                      boundaryCount={1}
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Container>

      {/* Details Dialog */}
      <Dialog
        open={Boolean(selectedDoc)}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedDoc && (
          <>
            <DialogTitle>
              <Typography variant="h6" fontWeight={600}>
                {selectedDoc.filename}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Hochgeladen am {formatDate(selectedDoc.uploadDate)}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Zusammenfassung
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Gesamt-Steine
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {selectedDoc.totalBricks || 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Geschützt
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color={selectedDoc.protectedBricks !== '0' && selectedDoc.protectedBricks !== 'ANALYZING' ? 'error.main' : 'text.primary'}
                    >
                      {selectedDoc.protectedBricks || '0'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Ungeschützt
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="success.main">
                      {selectedDoc.unprotectedBricks || 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {selectedDoc.bricksData && selectedDoc.bricksData.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Stein-ID</strong></TableCell>
                        <TableCell><strong>Beschreibung</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Details</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedDoc.bricksData.map((brick: AnalyzedBrick, index: number) => (
                        <TableRow key={index}>
                          <TableCell>{brick.id}</TableCell>
                          <TableCell>{brick.description}</TableCell>
                          <TableCell>
                            <Chip
                              label={brick.protected ? 'Geschützt' : 'Ungeschützt'}
                              color={brick.protected ? 'error' : 'success'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{brick.protectionDetails}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Schließen</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Documents;
